// eslint-disable-next-line no-restricted-imports
import { Flex, Paper, Skeleton, Stack, Title, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconInfoCircle } from '@tabler/icons-react';
import { match } from 'ts-pattern';

import { formatDollarsSplitParts } from '~/domains/common/utils/formatters';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import dt from '~/testing';
import { Text } from '~/domains/design-system/Text';

import { useWalletBalance } from '../hooks/useWalletBalance';

type BalanceBoxProps = {
  variant?: 'availableBalance' | 'withdrawableBalance' | 'refundableBalance';
};

function BalanceBox({ variant = 'withdrawableBalance' }: BalanceBoxProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const { data, isLoading } = useWalletBalance();

  const balance = match(variant)
    .with('withdrawableBalance', () => data?.withdrawableBalance)
    .with('refundableBalance', () => data?.refundableBalance)
    .otherwise(() => data?.availableBalance);

  return (
    // zIndex fixes the Tooltip being cut off by the Skeleton
    <Skeleton visible={isLoading} sx={{ zIndex: 1 }}>
      <Paper
        bg={theme.white}
        /* eslint-disable-line local-rules/use-theme-tokens */ px={18}
        py={theme.other.spacing._12}
        withBorder
        sx={{
          borderColor: theme.colors.gray[3],
        }}
      >
        <Flex align="center" justify="space-between" direction="row">
          <Flex gap={theme.other.spacing._4}>
            <Text
              data-test-id={dt.payments.components.balanceBoxTypeText}
              color={theme.colors.gray[9]}
              variant="subhead-large"
            >
              {(() => {
                if (variant === 'withdrawableBalance') {
                  return t('withdrawableBalance');
                }
                if (variant === 'refundableBalance') {
                  return t('refundableBalance');
                }
                return t('currentBalance');
              })()}
            </Text>
            {variant === 'withdrawableBalance' || variant === 'refundableBalance' ? (
              <Tooltip
                position="bottom"
                multiline
                label={
                  <Stack spacing={theme.other.spacing._4} p={theme.other.spacing._8} maw={265}>
                    <Title fz={14} lh="20px">
                      {variant === 'refundableBalance'
                        ? t('refundableBalanceTooltipTitle')
                        : t('withdrawableBalanceTooltipTitle')}
                    </Title>
                    <Text c="gray.9" fz={12} lh="16px" color="red">
                      {variant === 'refundableBalance'
                        ? t('refundableBalanceTooltipText')
                        : t('withdrawableBalanceTooltipText')}
                    </Text>
                  </Stack>
                }
              >
                <IconInfoCircle color={theme.colors.gray[6]} size={16} />
              </Tooltip>
            ) : null}
          </Flex>
          <Text
            c="gray.9"
            variant="headline-xxl"
            data-test-id={dt.payments.components.balanceBoxAmountText}
          >
            {typeof balance === 'number' ? formatDollarsSplitParts(balance).formattedWhole : '-'}
            <Text component="sup" variant="subhead-medium" top="-1em">
              {typeof balance === 'number'
                ? formatDollarsSplitParts(balance).formattedDecimal
                : '-'}
            </Text>
          </Text>
        </Flex>
      </Paper>
    </Skeleton>
  );
}

export { BalanceBox };
